/**
 * Actions bar for Drafts tab.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { CheckSquare, Square } from 'lucide-react';

import { Button } from '@/components/ui';
import { PreparedDropsUndoButton } from '@/features/prepared-drops-undo';

import { CountryGroupSortToggle } from './CountryGroupSortToggle';
import { DraftsSelectionActionButtons } from './DraftsSelectionActionButtons';

interface DraftsActionsBarProps {
  hasSelection: boolean;
  selectedCount: number;
  totalCount: number;
  allSelected: boolean;
  isBusy: boolean;
  bulkSendRunning: boolean;
  bulkSendStatus: { sent: number; total: number; failed: number };
  onToggleAll: () => void;
  onDelete: () => void;
  onBulkEdit: () => void;
  onDuplicate?: () => void;
  onSaveAsPreset?: () => void;
  onApply?: () => void;
  hasCopiedValues?: boolean;
  onBulkSend: () => void;
  onCreateSmswiserDraft?: () => void;
  smswiserDraftCount?: number;
  onClearSelection: () => void;
  listGroupByCountry?: boolean;
  onListGroupByCountryChange?: (value: boolean) => void;
}

export const DraftsActionsBar: FC<DraftsActionsBarProps> = ({
  hasSelection,
  selectedCount,
  totalCount,
  allSelected,
  isBusy,
  bulkSendRunning,
  bulkSendStatus,
  onToggleAll,
  onDelete,
  onBulkEdit,
  onDuplicate,
  onSaveAsPreset,
  onApply,
  hasCopiedValues,
  onBulkSend,
  onCreateSmswiserDraft,
  smswiserDraftCount,
  onClearSelection,
  listGroupByCountry = false,
  onListGroupByCountryChange,
}) => (
  <div className="shrink-0 border-b border-border/40 bg-muted/10 px-4 py-2">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={onToggleAll}
          disabled={isBusy || totalCount === 0}
          aria-label="Select all drafts"
        >
          {allSelected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
          Select all
          <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-medium tabular-nums">{totalCount}</span>
        </Button>
        {onListGroupByCountryChange != null && (
          <CountryGroupSortToggle active={listGroupByCountry} onChange={onListGroupByCountryChange} />
        )}
        {/* LEFT group deliberately: a delete clears the selection, so the right
         *  group unmounts at the exact moment undo is wanted. */}
        <PreparedDropsUndoButton />
      </div>

      {hasSelection ? (
        <DraftsSelectionActionButtons
          selectedCount={selectedCount}
          isBusy={isBusy}
          bulkSendRunning={bulkSendRunning}
          onDelete={onDelete}
          onBulkEdit={onBulkEdit}
          onDuplicate={onDuplicate}
          onSaveAsPreset={onSaveAsPreset}
          onApply={onApply}
          hasCopiedValues={hasCopiedValues}
          onBulkSend={onBulkSend}
          onCreateSmswiserDraft={onCreateSmswiserDraft}
          smswiserDraftCount={smswiserDraftCount}
          onClearSelection={onClearSelection}
        />
      ) : (
        <div className="text-xs text-muted-foreground">
          {bulkSendRunning
            ? `Bulk send… ${bulkSendStatus.sent}/${bulkSendStatus.total} (failed: ${bulkSendStatus.failed})`
            : 'Tip: select drafts to bulk edit or bulk send.'}
        </div>
      )}
    </div>
  </div>
);
